import { useEffect, useId, useRef, type CSSProperties, type PointerEvent, type ReactNode } from 'react';
import { FACTION_SPECS } from '@tb/shared';
import type { Faction } from '@tb/shared';

import { ICONS, type IconName } from './icons';

// ---------------------------------------------------------------------------
// The handful of parts every screen is built from. Status always carries a
// word or a shape as well as a colour, so it survives colour blindness and a
// glance in bad light.
//
// A part's tone is one class, tone-ok or tone-bad and so on, which base.css
// defines once: its ink, its edge, its wash, and a dashed edge for what is
// only inferred. Each part reads the tone it is given and nothing else, so a
// tag inside a red notice is not red unless it says so.
// ---------------------------------------------------------------------------

export type Tone = 'plain' | 'ok' | 'warn' | 'bad' | 'intel' | 'mil' | 'sel' | 'toll' | 'contaminated' | 'stopped';

/** Class names, with the empty and false ones left out. */
export const cx = (...names: (string | false | null | undefined)[]) => names.filter(Boolean).join(' ');

/** What every part passes through to its element: a class to place it, a title, and data attributes for tests and scripts. */
interface Common { className?: string; title?: string; style?: CSSProperties; 'aria-label'?: string; 'aria-hidden'?: boolean | 'true'; [data: `data-${string}`]: string | number | boolean | undefined }

/** A word or a figure on a small plate. Given an action, it is a button. */
export function Tag({ tone = 'plain', children, onClick, className, ...rest }: Common & { tone?: Tone; children: ReactNode; onClick?: () => void }) {
  const cls = cx('tag', `tone-${tone}`, className);
  return onClick
    ? <button type="button" className={cls} onClick={onClick} {...rest}>{children}</button>
    : <span className={cls} {...rest}>{children}</span>;
}

/** How many are waiting behind a control; with nothing to count, a dot that something is. */
export function Badge({ tone = 'sel', children, className, ...rest }: Common & { tone?: Tone; children?: ReactNode }) {
  return <b className={cx('badge', `tone-${tone}`, children == null && 'dot', className, children != null && 'mono')} {...rest}>{children}</b>;
}

/**
 * A bar against its limit, and the figure in words for a screen reader. With
 * a label it carries its own heading row; without one it is a bare track, for
 * a row that already says what it measures. A mark scribes a line across it
 * (where a city falls); unknown hatches the share nobody has seen.
 */
export function Meter({ label, value, max, tone, text, size = 'regular', mark, unknown, role = 'meter', className, ...rest }: Common & {
  label?: ReactNode; value: number; max: number; tone?: Tone; text?: ReactNode;
  size?: 'regular' | 'compact' | 'thin'; mark?: number; unknown?: number;
  role?: 'meter' | 'progressbar' | 'presentation';
}) {
  const f = max > 0 ? Math.max(0, Math.min(1, value / max)) : 0;
  const t = tone ?? (f > 0.6 ? 'ok' : f > 0.3 ? 'warn' : 'bad');
  const share = (n: number) => `${Math.max(0, Math.min(1, n)) * 100}%`;
  const read = role === 'presentation'
    ? { 'aria-hidden': true }
    : { role, 'aria-valuemin': 0, 'aria-valuemax': max, 'aria-valuenow': value, 'aria-label': typeof label === 'string' ? label : undefined };
  return (
    <span className={cx('meter', `tone-${t}`, size !== 'regular' && size, className)} {...read} {...rest}>
      {label != null && (
        <span className="meter-head">
          <span className="meter-label">{label}</span>
          <span className="meter-text mono">{text ?? `${Math.round(value)}/${Math.round(max)}`}</span>
        </span>
      )}
      <span className="meter-track">
        <i className="meter-fill" style={{ width: share(f) }} />
        {unknown != null && unknown > 0 && <i className="meter-unknown" style={{ width: share(unknown) }} />}
        {mark != null && <i className="meter-mark" style={{ left: share(mark) }} />}
      </span>
    </span>
  );
}

/** Something under way whose end is not known: a sweep, never a figure. */
export function Activity({ tone = 'warn', className, ...rest }: Common & { tone?: Tone }) {
  return <span className={cx('activity', `tone-${tone}`, className)} aria-hidden="true" {...rest}><i /></span>;
}

/**
 * A labelled figure, the unit dimmer than the number. A tile stacks the label
 * over the figure; inline sets them on one line, for a strip over the world;
 * hero is the one figure a screen is about.
 */
export function Stat({ k, v, unit, tone, size = 'tile', onClick, children, className, ...rest }: Common & {
  k: ReactNode; v: ReactNode; unit?: ReactNode; tone?: Tone; size?: 'tile' | 'inline' | 'hero';
  onClick?: () => void; children?: ReactNode; 'aria-expanded'?: boolean; disabled?: boolean; role?: 'status';
}) {
  const cls = cx('stat', size !== 'tile' && size, tone && `tone-${tone}`, className);
  const body = <>
    <span className="stat-k">{k}</span>
    <span className="stat-v mono">{v}{unit != null && <small className="stat-unit">{unit}</small>}</span>
    {children}
  </>;
  return onClick
    ? <button type="button" className={cls} onClick={onClick} {...rest}>{body}</button>
    : <span className={cls} {...rest}>{body}</span>;
}

const MARK: Record<Tone, IconName> = {
  plain: 'dot', ok: 'check', warn: 'caution', bad: 'warning', intel: 'info', mil: 'dot', sel: 'dot', toll: 'dot', contaminated: 'caution', stopped: 'caution',
};

/**
 * Something the commander should know before acting, with its mark. A block
 * stands on its own plate; inline is a line of text in the tone, under the
 * control it is about.
 */
export function Notice({ tone = 'plain', inline, children, role, className, ...rest }: Common & {
  tone?: Tone; inline?: boolean; children: ReactNode; role?: 'alert' | 'status';
}) {
  const live = role ?? (tone === 'bad' ? 'alert' : undefined);
  const mark = <Icon name={MARK[tone]} className="notice-mark" />;
  return inline
    ? <p className={cx('notice', 'inline', `tone-${tone}`, className)} role={live} {...rest}>{mark}<span>{children}</span></p>
    : <div className={cx('notice', `tone-${tone}`, className)} role={live} {...rest}>{mark}<div>{children}</div></div>;
}

/** A fault or a hold, over everything at the top of the screen, until it clears. */
export function Toast({ tone = 'bad', children, className, ...rest }: Common & { tone?: 'bad' | 'ok'; children: ReactNode; role?: 'status' | 'alert'; 'aria-live'?: 'polite' }) {
  return <div className={cx('toast', `tone-${tone}`, className)} {...rest}>{children}</div>;
}

/** Where a list would be: what is not there, and sometimes why. */
export function Empty({ as: As = 'p', children, className, ...rest }: Common & { as?: 'p' | 'li' | 'span' | 'div'; children: ReactNode }) {
  return <As className={cx('empty', className)} {...rest}>{children}</As>;
}

// --- marks ------------------------------------------------------------------------

/**
 * A drawn mark from the house set (ui/icons.ts), at the size of the text it
 * sits in. With a label it is an image and says so; without one it is
 * decoration, and the words beside it, or the control's own label, carry it.
 */
export function Icon({ name, label, className, ...rest }: Common & { name: IconName; label?: string }) {
  const def: { d: string; fill?: boolean } = ICONS[name];
  return (
    <svg className={cx('glyph', className)} data-icon={name} viewBox="0 0 24 24" focusable="false"
      role={label ? 'img' : undefined} aria-label={label} aria-hidden={label ? undefined : true}
      fill={def.fill ? 'currentColor' : 'none'} stroke={def.fill ? 'none' : 'currentColor'}
      strokeWidth="1.6" strokeLinecap="square" strokeLinejoin="miter" {...rest}>
      <path d={def.d} />
    </svg>
  );
}

// --- surfaces -----------------------------------------------------------------

/**
 * What a surface is made of. Glass floats over the world; raised is a panel, a
 * card or a dialog standing over the console; recessed is set into a panel;
 * housing is the lobby's equipment. kit.css draws each once.
 */
export type Material = 'glass' | 'raised' | 'recessed' | 'housing';

/**
 * A dialog over everything: the page's own modal dialog, so focus stays inside
 * it, the page under it waits, and nothing done in it reaches the page. Escape
 * or a click outside asks it to close through onClose; one that must be
 * answered (a match's end) passes none, and they do nothing. Its actions stay
 * in view while its body scrolls.
 */
export function Dialog({ onClose, size = 'md', material = 'raised', modal = true, kicker, title, aside, actions, children, className, ...rest }: Common & {
  onClose?: () => void; size?: 'sm' | 'md' | 'lg'; material?: Material;
  /** False only where the dialog is drawn in place, as the visual register draws it. */
  modal?: boolean;
  kicker?: ReactNode; title?: ReactNode; aside?: ReactNode; actions?: ReactNode; children?: ReactNode;
  'aria-describedby'?: string;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const id = useId();
  useEffect(() => {
    const d = ref.current;
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    if (d && !d.open) { if (modal) d.showModal(); else d.show(); }
    return () => {
      d?.close();
      // Unmount removes the dialog before this cleanup; restore its opener explicitly.
      if (modal && previous?.isConnected) previous.focus({ preventScroll: true });
    };
  }, [modal]);
  return (
    <dialog
      ref={ref}
      className={cx('dialog', material, size, className)}
      aria-labelledby={title != null ? id : undefined}
      onCancel={(e) => { e.preventDefault(); onClose?.(); }}
      // The dialog fills its own box, so a click on the dialog itself is a click on the backdrop.
      onClick={(e) => { e.stopPropagation(); if (e.target === e.currentTarget) onClose?.(); }}
      onPointerDown={(e) => e.stopPropagation()}
      {...rest}
    >
      {(kicker != null || title != null || aside != null) && (
        <header className="dialog-head">
          <div>
            {kicker != null && <span className="eyebrow">{kicker}</span>}
            {title != null && <h2 id={id} className="dialog-title">{title}</h2>}
          </div>
          {aside}
        </header>
      )}
      <div className="dialog-body">{children}</div>
      {actions != null && <footer className="dialog-actions">{actions}</footer>}
    </dialog>
  );
}

// --- controls -----------------------------------------------------------------

export type ChoiceSize = 'tile' | 'strip' | 'wide' | 'chip';

/**
 * One of a set to pick: a weapon, a warhead, a wave, a stance, a refit, a
 * status card, a unit from the reserve. Its state is the ARIA one (checked in
 * a radio group, pressed for a toggle), and kit.css paints it once for every
 * choice. Out is there to inspect but not to use; hazard carries a warning (a
 * nuclear warhead). A tile stacks art, name, figure and a line under it;
 * strip is the same, narrow, for the strips along a city; wide is a line to
 * read; chip is small and inline.
 */
export function Choice({ on, toggle, out, hazard, size = 'tile', mark, art, name, figure, meta, children, className, style, ...rest }: Common & {
  on: boolean; toggle?: boolean; out?: boolean; hazard?: boolean; size?: ChoiceSize;
  /** The colour of the edge, where a choice has one of its own (a status card's signal). */
  mark?: string;
  art?: ReactNode; name?: ReactNode; figure?: ReactNode; meta?: ReactNode; children?: ReactNode;
  disabled?: boolean; onClick?: () => void; onPointerDown?: (e: PointerEvent<HTMLButtonElement>) => void;
}) {
  return (
    <button
      type="button"
      role={toggle ? undefined : 'radio'}
      aria-checked={toggle ? undefined : on}
      aria-pressed={toggle ? on : undefined}
      className={cx('choice', size, out && 'out', hazard && 'hazard', className)}
      style={mark ? { ...style, ['--mark' as string]: mark } : style}
      {...rest}
    >
      {art != null && <span className="choice-art" aria-hidden="true">{art}</span>}
      {name != null && <span className="choice-name">{name}</span>}
      {figure != null && <span className="choice-figure mono">{figure}</span>}
      {meta != null && <span className="choice-meta">{meta}</span>}
      {children}
    </button>
  );
}

/** A set of choices: one radio group (or a group of toggles), laid out for its size. */
export function Choices({ label, size = 'tile', toggle, columns, children, className, style, ...rest }: Common & {
  label: string; size?: ChoiceSize; toggle?: boolean; columns?: number; children: ReactNode;
}) {
  return (
    <div role={toggle ? 'group' : 'radiogroup'} aria-label={label} className={cx('choices', size, className)}
      style={columns ? { ...style, ['--cols' as string]: columns } : style} {...rest}>
      {children}
    </div>
  );
}

/**
 * A few settings side by side, one of them on: the radar's posture, a watched
 * city's stance, the wave a round flies in. An option can be shut on its own
 * (a warhead none are left of). Wide shares the width out, wrapping if it must.
 */
export function Segmented<T extends string | number>({ label, value, options, onChange, disabled, size = 'regular', wide, className, ...rest }: Common & {
  label: string; value: T | undefined; options: readonly { value: T; label: ReactNode; title?: string; disabled?: boolean }[];
  onChange: (value: T) => void; disabled?: boolean; size?: 'regular' | 'compact'; wide?: boolean;
}) {
  return (
    <div role="radiogroup" aria-label={label} className={cx('segmented', size !== 'regular' && size, wide && 'wide', className)} {...rest}>
      {options.map((o) => (
        <button key={o.value} type="button" role="radio" aria-checked={value === o.value} className="segment"
          disabled={disabled || o.disabled} title={o.title} onClick={() => onChange(o.value)}>{o.label}</button>
      ))}
    </div>
  );
}

export type TabKind = 'selected' | 'page' | 'step' | 'expanded';

/**
 * One of a row of tabs, lit while it is the one in view. What it is decides
 * how it says so: a page of a panel is selected in its tab list, a place on the
 * command ribbon is the current page, an attack stage the current step, and a
 * tab that opens a sheet is expanded while the sheet is open. kit.css lights
 * all four alike.
 */
export function Tab({ on, kind = 'selected', size = 'md', children, className, ...rest }: Common & {
  on: boolean; kind?: TabKind; size?: 'sm' | 'md' | 'lg'; children: ReactNode;
  disabled?: boolean; onClick?: () => void; 'aria-disabled'?: boolean; 'aria-controls'?: string; 'aria-haspopup'?: 'dialog';
}) {
  return (
    <button
      type="button"
      role={kind === 'selected' ? 'tab' : undefined}
      aria-selected={kind === 'selected' ? on : undefined}
      aria-current={kind === 'page' ? on : kind === 'step' && on ? 'step' : undefined}
      aria-expanded={kind === 'expanded' ? on : undefined}
      className={cx('tab', size, className)}
      {...rest}
    >
      {children}
    </button>
  );
}

/** The tabs over the pages of one panel. Wide shares the width out among them. */
export function Tabs({ label, wide, children, className, ...rest }: Common & { label: string; wide?: boolean; children: ReactNode }) {
  return <div role="tablist" aria-label={label} className={cx('tabs', wide && 'wide', className)} {...rest}>{children}</div>;
}

export const pct = (n: number) => `${Math.round(n * 100)}%`;
export const sideName = (f: string) => FACTION_SPECS[f as Faction]?.name ?? f;
